import type { RosterColumn } from '../../services/roster/roster.columns';
import { buildRosterCsv, rosterCsvFilename } from '../../services/roster/roster.csv';
import { RosterAction, rosterComingSoon, RosterRecord } from '../../services/roster/roster.types';
import { ToastContext } from '../../services/toast/toast';
import { RosterStore } from '../../stores/roster/roster.store';
import { BaseButton } from '../base/button/button';
import { ComingSoon } from '../base/coming-soon/coming-soon';
import { downloadCsvBlob } from '../data-table/components/table-export/csv-utils';
import { DataTablePresenter } from '../data-table/data-table.presenter';
import { rosterDisplayText } from './roster-display';
import { mdiCancel, mdiDownload, mdiTagOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React, { useContext } from 'react';
import { rosterNoun, useRosterDisable } from './roster-actions';
import { AddTagForm } from './roster-bulk-dialogs';
import { useRosterDialog } from './roster-dialog';
import * as S from './roster-table.styles';

interface IRosterBulkBarProps {
    store: RosterStore;
    presenter: DataTablePresenter<RosterRecord>;
    getVisibleColumns: () => RosterColumn[];
}

/**
 * The bar over the table while rows are selected: how many, what can be done to them, and a way
 * out. Add tag and Export selected for everyone; Disable for ENTER ops. The design's "Set
 * automation" is left out: billing automation is turned off on every save (see
 * `rosterProviderDraft`). A write the data source cannot do is disabled with a hint, never hidden
 * (NS-4).
 */
export const RosterBulkBar = observer(
    ({ store, presenter, getVisibleColumns }: IRosterBulkBarProps): React.ReactElement => {
        const dialog = useRosterDialog();
        const confirmDisable = useRosterDisable(store);
        const toast = useContext(ToastContext);
        const ids = Object.keys(presenter.selectedIds).filter(id => presenter.selectedIds[id]);

        if (ids.length === 0) {
            return <></>;
        }

        const isProviders = store.tab === 'providers';
        const noun = rosterNoun(store.tab, ids.length);
        const capabilities = store.dataSource.capabilities;
        // An action runs only when the source can write and its endpoint exists; otherwise it is
        // shown disabled under a "Coming soon" hint (NS-4).
        const available = (action: RosterAction): boolean => capabilities.write && !rosterComingSoon(capabilities, action);
        const busy = store.bulkBusy;
        const close = (): void => dialog.hide();
        const notify = (message: string, ok = true): void => toast.notify?.(message, 'right', ok ? 'light' : 'warning');
        const failure = (what: string, error: unknown): void =>
            notify(`${what} failed: ${error instanceof Error ? error.message : 'request failed'}.`, false);

        const addTag = (): void =>
            dialog.show({
                props: { onClose: close, renderTitle: 'Add tag' },
                Component: (
                    <AddTagForm
                        count={ids.length}
                        noun={noun}
                        onCancel={close}
                        onSubmit={async (tagName): Promise<void> => {
                            try {
                                await store.addTag(ids, tagName);
                                close();
                                notify(`Tag "${tagName}" added to ${ids.length} ${noun}.`);
                            } catch (error) {
                                failure('Adding the tag', error);
                            }
                        }}
                    />
                ),
            });

        const exportSelected = async (): Promise<void> => {
            const tab = store.tab;
            const columns = getVisibleColumns();
            try {
                const records = await store.recordsByIds(ids);
                const csv = buildRosterCsv(columns, records, (record, key) => rosterDisplayText(tab, record, key));
                downloadCsvBlob(csv, rosterCsvFilename(`${tab}-selected`, new Date()));
                notify(`${records.length} ${records.length === 1 ? 'row' : 'rows'} exported.`);
            } catch (error) {
                failure('Export', error);
            }
        };

        const disable = (): void =>
            confirmDisable(ids, stillSelected =>
                presenter.onSelectedUpdate(Object.fromEntries(stillSelected.map(id => [id, true]))),
            );

        const writeAction = (
            action: RosterAction,
            label: string,
            icon: string,
            onClick: () => void,
            danger = false,
        ): React.ReactElement => {
            const canRun = available(action);
            const Button = danger ? S.DangerButton : BaseButton;
            const button = (
                <Button variant="outlined" size="small" onClick={onClick} disabled={busy || !canRun}>
                    <S.BulkButtonContent>
                        <Icon path={icon} size={0.65} />
                        {label}
                    </S.BulkButtonContent>
                </Button>
            );
            return canRun ? (
                button
            ) : (
                <ComingSoon hint={`${label.toLowerCase()} from this account`}>{button}</ComingSoon>
            );
        };

        return (
            <S.BulkBar>
                <S.BulkLabel aria-live="polite">{ids.length} selected</S.BulkLabel>
                <S.BulkActions>
                    {capabilities.tags && writeAction('tag', 'Add tag', mdiTagOutline, addTag)}
                    <BaseButton variant="outlined" size="small" onClick={exportSelected} disabled={busy}>
                        <S.BulkButtonContent>
                            <Icon path={mdiDownload} size={0.65} />
                            Export selected
                        </S.BulkButtonContent>
                    </BaseButton>
                    {store.role === 'ops' && writeAction('disable', 'Disable', mdiCancel, disable, true)}
                </S.BulkActions>
                <S.ClearSelection>
                    <BaseButton variant="text" size="small" onClick={(): void => presenter.onSelectedUpdate({})}>
                        Clear selection
                    </BaseButton>
                </S.ClearSelection>
            </S.BulkBar>
        );
    },
);
